// In dev, Vite proxy routes API calls to the backend Workers (same origin).
// In production, VITE_API_URL points to the API gateway domain.
import {
  ACTIVE_TENANT_ORG_STORAGE_KEY,
  ACTIVE_TENANT_STORAGE_KEY,
  emitActiveTenantContextChanged,
} from "@/lib/active-tenant-context";

const API_URL = import.meta.env.VITE_API_URL ?? "";
const DEFAULT_TIMEOUT_MS = 15_000;
export { ACTIVE_TENANT_ORG_STORAGE_KEY, ACTIVE_TENANT_STORAGE_KEY };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let sessionExpiredHandler: ((details: { redirectPath: string; requestPath: string }) => void) | null = null;
let rateLimitHandler: ((details: { retryAfterSeconds: number; requestPath: string }) => void) | null = null;

export function buildApiUrl(path: string): string {
  return `${API_URL}${path}`;
}

function shouldSetJsonContentType(body: BodyInit | null | undefined): boolean {
  if (body === null || body === undefined) {
    return false;
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return false;
  }

  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return false;
  }

  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
    return false;
  }

  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) {
    return false;
  }

  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream) {
    return false;
  }

  return true;
}

/** Typed fetch wrapper with auth credentials and error handling */
export interface ApiFetchOptions extends RequestInit {
  activeTenantId?: string | null;
  skipSessionExpiredModal?: boolean;
  skipActiveTenantHeader?: boolean;
}

function parseRetryAfterSeconds(headerValue: string | null): number {
  if (!headerValue) {
    return 60;
  }

  const seconds = Number.parseInt(headerValue, 10);
  if (Number.isFinite(seconds) && seconds > 0) {
    return seconds;
  }

  const retryAtMs = Date.parse(headerValue);
  if (Number.isFinite(retryAtMs)) {
    const deltaSeconds = Math.ceil((retryAtMs - Date.now()) / 1000);
    if (deltaSeconds > 0) {
      return deltaSeconds;
    }
  }

  return 60;
}

function getCurrentAppPath(): string {
  if (typeof window === "undefined") {
    return "/";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getTenantStorage(): Storage | null {
  const storage = globalThis.localStorage;
  if (
    !storage
    || typeof storage.getItem !== "function"
    || typeof storage.setItem !== "function"
    || typeof storage.removeItem !== "function"
  ) {
    return null;
  }

  return storage;
}

export function getStoredActiveTenantId(): string | null {
  const storage = getTenantStorage();
  if (!storage) {
    return null;
  }

  const organizationId = storage.getItem(ACTIVE_TENANT_ORG_STORAGE_KEY)?.trim() ?? "";
  if (!organizationId || !UUID_RE.test(organizationId)) {
    if (organizationId) {
      storage.removeItem(ACTIVE_TENANT_ORG_STORAGE_KEY);
    }
    storage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    return null;
  }

  const value = storage.getItem(ACTIVE_TENANT_STORAGE_KEY)?.trim() ?? "";
  if (value && !UUID_RE.test(value)) {
    storage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    return null;
  }
  return value.length > 0 ? value : null;
}

export function getStoredActiveTenantOrganizationId(): string | null {
  const storage = getTenantStorage();
  if (!storage) {
    return null;
  }

  const value = storage.getItem(ACTIVE_TENANT_ORG_STORAGE_KEY)?.trim() ?? "";
  if (value && !UUID_RE.test(value)) {
    storage.removeItem(ACTIVE_TENANT_ORG_STORAGE_KEY);
    return null;
  }
  return value.length > 0 ? value : null;
}

function emitIfActiveTenantContextChanged(
  previousTenantId: string | null,
  previousOrganizationId: string | null,
): void {
  const storage = getTenantStorage();
  if (!storage) {
    return;
  }

  const nextTenantId = storage.getItem(ACTIVE_TENANT_STORAGE_KEY);
  const nextOrganizationId = storage.getItem(ACTIVE_TENANT_ORG_STORAGE_KEY);
  if (nextTenantId !== previousTenantId || nextOrganizationId !== previousOrganizationId) {
    emitActiveTenantContextChanged();
  }
}

export function setStoredActiveTenantId(tenantId: string | null): void {
  const storage = getTenantStorage();
  if (!storage) {
    return;
  }

  const previousTenantId = storage.getItem(ACTIVE_TENANT_STORAGE_KEY);
  const previousOrganizationId = storage.getItem(ACTIVE_TENANT_ORG_STORAGE_KEY);

  if (tenantId?.trim()) {
    storage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId.trim());
    storage.removeItem(ACTIVE_TENANT_ORG_STORAGE_KEY);
  } else {
    storage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    storage.removeItem(ACTIVE_TENANT_ORG_STORAGE_KEY);
  }

  emitIfActiveTenantContextChanged(previousTenantId, previousOrganizationId);
}

function normalizeTenantId(value: string | null | undefined): string | null {
  const normalized = value?.trim() ?? "";
  return normalized && UUID_RE.test(normalized) ? normalized : null;
}

export function setStoredActiveTenantContext(tenantId: string | null, organizationId: string | null): void {
  const storage = getTenantStorage();
  if (!storage) {
    return;
  }

  const previousTenantId = storage.getItem(ACTIVE_TENANT_STORAGE_KEY);
  const previousOrganizationId = storage.getItem(ACTIVE_TENANT_ORG_STORAGE_KEY);

  const normalizedTenantId = normalizeTenantId(tenantId);
  const normalizedOrganizationId = normalizeTenantId(organizationId);
  if (normalizedTenantId && normalizedOrganizationId) {
    storage.setItem(ACTIVE_TENANT_STORAGE_KEY, normalizedTenantId);
    storage.setItem(ACTIVE_TENANT_ORG_STORAGE_KEY, normalizedOrganizationId);
    emitIfActiveTenantContextChanged(previousTenantId, previousOrganizationId);
    return;
  }

  storage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  storage.removeItem(ACTIVE_TENANT_ORG_STORAGE_KEY);
  emitIfActiveTenantContextChanged(previousTenantId, previousOrganizationId);
}

export function setSessionExpiredHandler(
  handler: ((details: { redirectPath: string; requestPath: string }) => void) | null,
): void {
  sessionExpiredHandler = handler;
}

export function setRateLimitHandler(
  handler: ((details: { retryAfterSeconds: number; requestPath: string }) => void) | null,
): void {
  rateLimitHandler = handler;
}

export async function apiFetch<T>(
  path: string,
  options?: ApiFetchOptions,
): Promise<T> {
  const {
    activeTenantId: activeTenantIdOverride,
    skipActiveTenantHeader = false,
    skipSessionExpiredModal = false,
    ...requestOptions
  } = options ?? {};
  const url = buildApiUrl(path);
  const activeTenantId = skipActiveTenantHeader
    ? null
    : activeTenantIdOverride === undefined
      ? getStoredActiveTenantId()
      : normalizeTenantId(activeTenantIdOverride);
  const response = await fetch(url, {
    ...requestOptions,
    credentials: "include", // HttpOnly cookies for auth
    signal: requestOptions.signal ?? AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    headers: {
      ...(shouldSetJsonContentType(requestOptions.body) ? { "Content-Type": "application/json" } : {}),
      ...(activeTenantId ? { "X-Tenant-Id": activeTenantId } : {}),
      ...requestOptions.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Unknown error" }));
    const parsed = body as { error?: string; code?: string };
    if (response.status === 401 && !skipSessionExpiredModal) {
      sessionExpiredHandler?.({
        redirectPath: getCurrentAppPath(),
        requestPath: path,
      });
    }
    const retryAfterSeconds = response.status === 429
      ? parseRetryAfterSeconds(response.headers.get("Retry-After"))
      : undefined;
    if (response.status === 429) {
      rateLimitHandler?.({
        retryAfterSeconds: retryAfterSeconds ?? 60,
        requestPath: path,
      });
    }
    throw new ApiError(response.status, parsed.error ?? "Request failed", path, parsed.code, retryAfterSeconds);
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  public readonly errorCode?: string;
  public readonly retryAfterSeconds?: number;

  constructor(
    public readonly status: number,
    message: string,
    public readonly path: string,
    errorCode?: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
    this.errorCode = errorCode;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}
